import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getFilm, exportFilmsCSV, exportFilmsExcel, deleteFilm, deleteMultipleFilms, assignFilmImages, refreshFilmImages, exportFilmIBS, exportFilmWeb, exportFilmWebFiltered, deleteAllFilmImages, deleteAllFilms, getLayoutsByModulo, generatePdf, refreshToken } from '../services/api';
import { Table, Form, Spinner, Alert, InputGroup, Row, Col, Dropdown, Pagination, Card, Button, OverlayTrigger, Popover, Modal, ProgressBar, Badge } from 'react-bootstrap';
import { useMediaQuery } from 'react-responsive';
import ActionToolbar from '../common/ActionToolbar';
import MobileListLayout from '../common/MobileListLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faSort, faSortUp, faSortDown, faTimes, faChartLine, faFilm, faBoxes, faEuroSign, faStar, faRecycle, faArchive, faPlus, faCog, faSearch, faFileInvoice, faWarehouse, faExclamationTriangle, faCheckCircle, faClock, faTheaterMasks, faBuilding, faCompactDisc, faHeart, faLaugh, faDragon, faSkull, faRocket, faMasksTheater, faMusic, faChild, faBook } from '@fortawesome/free-solid-svg-icons';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import BulkEditFilmModal from '../common/BulkEditFilmModal';
import ColumnOrderController from '../common/ColumnOrderController';
import UpdateGenreModal from '../common/UpdateGenreModal';
import SortableHeader from '../common/SortableHeader';
import StatsCards from '../common/StatsCards';
import { usePageTitle } from '../context/PageTitleContext';
import { getOptimalColumnWidth, getMinColumnWidth, autoFitColumns, autoResizeColumn, resetColumnWidths } from '../common/columnSizingUtils';

// Helper per la creazione delle colonne
const columnHelper = createColumnHelper();

// Colonne numeriche per i filtri avanzati
const numericColumns = ['id', 'anno', 'durata', 'prezzo_negozio_base', 'prezzo_negozio', 'prezzo_ibs', 'costo_acquisto', 'quantita', 'iva'];

// Colonne data per i filtri avanzati (Film non ha campi data)
const dateColumns = [];

// Definizione di tutte le colonne disponibili
const allColumnsMap = {
    id: "ID",
    titolo: "Titolo",
    immagine_url: "URL Immagine",
    anno: "Anno",
    genere: "Genere",
    regista: "Regista",
    attori: "Attori",
    trama: "Trama",
    durata: "Durata",
    casa_di_produzione: "Casa di Produzione",
    casa_di_distribuzione: "Distribuzione",
    categoria: "Categoria",
    supporto: "Supporto",
    sku: "SKU",
    barcode: "Barcode",
    barcode_ibs: "Barcode IBS",
    prezzo_negozio_base: "Prezzo Base",
    prezzo_negozio: "Prezzo Negozio",
    prezzo_ibs: "Prezzo IBS",
    condizione: "Condizione",
    quantita: "Quantità",
    versione_noleggio: "Versione Noleggio",
    versione_editoriale: "Versione Editoriale",
    fuori_catalogo: "Fuori Catalogo",
    iva: "IVA",
    costo_acquisto: "Costo Acquisto",

    ubicazione: "Ubicazione",
    fornitore_id: "ID Fornitore",
    tmdb_id: "ID TMDB",
    fornitore: "Fornitore", // Campo relazionale
    da_pubblicare_su_ibs: "Presente su IBS", // Nuovo campo
    in_vetrina: "In Vetrina", // Nuovo campo per la vetrina
};

// Colonne inizialmente visibili
const initialVisibleColumns = ['immagine_url', 'titolo', 'genere', 'regista', 'anno', 'quantita', 'fornitore', 'casa_di_produzione', 'casa_di_distribuzione', 'da_pubblicare_su_ibs', 'in_vetrina'];

// Componente per l'header riordinabile (spostato dentro FilmList)

const visibilityKey = 'table-visibility-films';

function FilmList() {
  const location = useLocation();
  const [films, setFilms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFilmIds, setSelectedFilmIds] = useState([]);
  const [isSelectAllFiltered, setIsSelectAllFiltered] = useState(false);
  const [quickSearch, setQuickSearch] = useState(() => {
    const saved = sessionStorage.getItem('list-filters-films');
    if (saved) {
      const filters = JSON.parse(saved);
      const gs = filters.find(f => f.id === '_global_search');
      return gs ? gs.value : '';
    }
    return '';
  });
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [showUpdateGenreModal, setShowUpdateGenreModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingIBS, setExportingIBS] = useState(false);
  const [exportingWeb, setExportingWeb] = useState(false);
  const [exportingAllWeb, setExportingAllWeb] = useState(false);
  const [populationProgress, setPopulationProgress] = useState({
    active: false,
    processed: 0,
    total: 0,
    current_film: '',
    status: 'idle', // idle, running, success, error
  });
  const [ratingUpdateProgress, setRatingUpdateProgress] = useState({
    active: false,
    processed: 0,
    total: 0,
    current_film: '',
    status: 'idle',
    updated: 0,
    skipped: 0,
    errors: 0
  });
  
  const [imageAssignProgress, setImageAssignProgress] = useState({
    active: false,
    processed: 0,
    total: 0,
    current_product: '',
    status: 'idle',
    success: 0,
    failed: 0,
    failed_report: []
  });
  
  const [imageRefreshProgress, setImageRefreshProgress] = useState({
    active: false,
    processed: 0,
    total: 0,
    current_product: '',
    status: 'idle',
    success: 0,
    failed: 0,
    low_quality: 0,
    improved_online: 0,
    failed_report: [],
    low_quality_report: []
  });
  const eventSourceRef = React.useRef(null);
  const ratingEventSourceRef = React.useRef(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showRatingReportModal, setShowRatingReportModal] = useState(false);
  const [ratingReport, setRatingReport] = useState({ updated_films: [], skipped_films: [], failed_films: [] });
  const isMobile = useMediaQuery({ maxWidth: 991 });
  const navigate = useNavigate();
  const [distinctValues, setDistinctValues] = useState({
    genere: [],
    categoria: [],
    supporto: [],
    condizione: [],
    anno: [],
    durata: [],
    prezzo_negozio_base: [],
    prezzo_negozio: [],
    prezzo_ibs: [],
    regista: [],
    attori: [],
    quantita: [],
    casa_di_produzione: [],
    ubicazione: [],
  });

  // TanStack Table states
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem(visibilityKey);
    if (saved) {
      return JSON.parse(saved);
    }
    return Object.keys(allColumnsMap).reduce((acc, key) => {
      acc[key] = initialVisibleColumns.includes(key);
      return acc;
    }, {});
  });
  const [sorting, setSorting] = useState(() => {
    const saved = sessionStorage.getItem('list-sorting-films');
    return saved ? JSON.parse(saved) : [];
  });
  const [columnFilters, setColumnFilters] = useState(() => {
    const saved = sessionStorage.getItem('list-filters-films');
    return saved ? JSON.parse(saved) : [];
  });
  const [columnOrder, setColumnOrder] = useState(() => {
    const allKeys = Object.keys(allColumnsMap);
    const savedOrderJSON = localStorage.getItem('table-column-order-films');
    
    if (savedOrderJSON) {
      let savedOrder = JSON.parse(savedOrderJSON);
      // Rimuoviamo 'select' per gestirlo a parte
      savedOrder = savedOrder.filter(c => c !== 'select');
      
      // Troviamo le colonne nuove che non sono nell'ordine salvato
      const newKeys = allKeys.filter(key => !savedOrder.includes(key));
      
      // Uniamo l'ordine salvato con le nuove chiavi e aggiungiamo 'select' all'inizio
      const finalOrder = ['select', ...savedOrder, ...newKeys];
      return finalOrder;
    }

    // Se non c'è niente di salvato, usiamo l'ordine di default
    const defaultOrder = ['select', ...allKeys];
    return defaultOrder;
  });
  const [columnSizing, setColumnSizing] = useState(() => {
    const saved = localStorage.getItem('table-column-sizing-films');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    sessionStorage.setItem('list-sorting-films', JSON.stringify(sorting));
  }, [sorting]);

  useEffect(() => {
    sessionStorage.setItem('list-filters-films', JSON.stringify(columnFilters));
  }, [columnFilters]);

  useEffect(() => {
    localStorage.setItem('table-column-order-films', JSON.stringify(columnOrder));
  }, [columnOrder]);

  useEffect(() => {
    localStorage.setItem('table-column-sizing-films', JSON.stringify(columnSizing));
  }, [columnSizing]);

  useEffect(() => {
    setIsSelectAllFiltered(false);
    table.toggleAllPageRowsSelected(false);
  }, [columnFilters]);


  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [totalFilms, setTotalFilms] = useState(0);
  const [serverStats, setServerStats] = useState({ nuovi: 0, usati: 0, fuori_catalogo: 0 });
  const [reportType, setReportType] = useState('all'); // all, in_stock, out_of_stock
  const [reportTitle, setReportTitle] = useState('Tutti i Film');
  const [lastReport, setLastReport] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Sticky columns configuration
  const stickyColumns = ['select', 'id'];

  // Funzione per forzare il ricaricamento dei dati
  const refetchFilms = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  // Auto-refresh quando si torna da FilmEdit con shouldRefresh=true
  useEffect(() => {
    if (location.state?.shouldRefresh) {
      refetchFilms();
      // Clear state to prevent repeated refreshes on subsequent navigations
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const filters = columnFilters.reduce((acc, filter) => {
                    const { id, value } = filter;

                    // Filtro avanzato dal popover colonna (Filter.js): "non vuoti"/"contiene"/
                    // "non contiene", tradotto negli operatori che il backend gia' supporta
                    // via suffisso campo__operatore.
                    if (value && typeof value === 'object' && !Array.isArray(value) && value.__advanced) {
                        if (value.__advanced === 'not_empty') {
                            acc[`${id}__isnot`] = null;
                        } else if (value.__advanced === 'contains') {
                            acc[`${id}__ilike`] = value.text;
                        } else if (value.__advanced === 'not_contains') {
                            acc[`${id}__not_ilike`] = value.text;
                        }
                    }
                    // Range filter for numbers/dates (array with [min, max])
                    else if (Array.isArray(value) && value.length === 2 && (value[0] || value[1])) {
                      const [min, max] = value;
                      
                      if (min) {
                        acc[`${id}__gte`] = numericColumns.includes(id) ? parseFloat(min) : min;
                      }
                      if (max) {
                        acc[`${id}__lte`] = numericColumns.includes(id) ? parseFloat(max) : max;
                      }
                    }
                    // Multi-select filter
                    else if (Array.isArray(value)) {
                        if (value.length > 0) {
                            acc[id] = { 'in': value };
                        }
                    } else if (typeof value === 'string' && value.toUpperCase() === 'N/A') {
                        acc[id] = null;
                    } else if (value !== undefined && value !== '') {
                        acc[id] = value;
                    }
                    return acc;
                }, {});

            const sortParams = sorting.map(sort => `${sort.id}:${sort.desc ? 'desc' : 'asc'}`);
            const stringifiedFilters = JSON.stringify(filters);


            const response = await getFilm(
                (currentPage - 1) * itemsPerPage,
                itemsPerPage,
                sortParams,
                stringifiedFilters // Importante: deve essere una stringa JSON
            );
            setFilms(response.data.film);
            setTotalFilms(response.data.total);
            if (response.data.facets) {
              setDistinctValues(response.data.facets);
            }
            if (response.data.stats) {
              setServerStats(response.data.stats);
            }
        } catch (err) {
            setError('Errore nel caricamento dei film.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };
    fetchData();
  }, [currentPage, itemsPerPage, sorting, columnFilters, refreshTrigger]);

  // reportType parte sempre da 'all' (non e' persistito) e questo effetto gira
  // anche al mount: senza lo skip qui sotto, ogni volta che si torna su FilmList
  // (es. dopo aver aperto/modificato un film) l'effetto cancellava incondizionatamente
  // qualunque filtro 'quantita' gia' ripristinato da sessionStorage, incluso uno scelto
  // a mano dal popover colonna. Deve agire solo su un cambio di reportType vero e
  // proprio (click sui bottoni di report rapido), non sul mount.
  const reportTypeMounted = useRef(false);
  useEffect(() => {
    if (!reportTypeMounted.current) {
      reportTypeMounted.current = true;
      return;
    }

    // Update filters based on report type
    setColumnFilters(prevFilters => {
      const otherFilters = prevFilters.filter(f => f.id !== 'quantita');
      if (reportType === 'in_stock') {
        return [...otherFilters, { id: 'quantita', value: { gt: 0 } }];
      }
      if (reportType === 'out_of_stock') {
        return [...otherFilters, { id: 'quantita', value: { eq: 0 } }];
      }
      return otherFilters;
    });

    // Update title based on report type
    if (reportType === 'in_stock') {
      setReportTitle('Film in Magazzino');
    } else if (reportType === 'out_of_stock') {
      setReportTitle('Film Esauriti');
    } else {
      setReportTitle('Tutti i Film');
    }
  }, [reportType]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setColumnFilters(prev => {
        const without = prev.filter(f => f.id !== '_global_search');
        if (quickSearch) return [...without, { id: '_global_search', value: quickSearch }];
        return without;
      });
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [quickSearch]);

  const warehouseValue = useMemo(() => {
    if (reportType !== 'in_stock') return 0;
    return films.reduce((acc, film) => {
        const cost = film.costo_acquisto || 0;
        const quantity = film.quantita || 0;
        return acc + (cost * quantity);
    }, 0);
  }, [films, reportType]);

  // Helper functions for badges and icons
  const compactBadgeStyle = { fontSize: '0.7rem', padding: '0.25em 0.45em', fontWeight: 600 };

  const getCondizioneBadge = (condizione) => {
    const condizioniConfig = {
      'Nuovo': { variant: 'success', icon: faCheckCircle, text: 'Nuovo' },
      'Usato - Ottime Condizioni': { variant: 'primary', icon: faStar, text: 'Usato · Ottimo' },
      'Usato - Buone Condizioni': { variant: 'info', icon: faStar, text: 'Usato · Buono' },
      'Usato - Discrete Condizioni': { variant: 'warning', icon: faClock, text: 'Usato · Discreto' },
      'Usato - Accettabile': { variant: 'danger', icon: faExclamationTriangle, text: 'Usato · Accett.' }
    };
    const config = condizioniConfig[condizione] || { variant: 'secondary', icon: null, text: condizione || 'N/A' };
    return (
      <Badge bg={config.variant} className="d-inline-flex align-items-center gap-1" style={compactBadgeStyle} title={condizione || ''}>
        {config.icon && <FontAwesomeIcon icon={config.icon} size="xs" />}
        {config.text}
      </Badge>
    );
  };

  const formatCurrency = (value) => {
    if (value == null) return 'N/A';
    const num = parseFloat(value);
    const formatted = num.toFixed(2);
    const colorClass = num < 0 ? 'text-danger' : num > 0 ? 'text-success' : 'text-muted';
    return <span className={colorClass}>€ {formatted}</span>;
  };

  const formatPrezzoVendita = (value) => {
    if (value == null) return 'N/A';
    return <span className="text-dark">€ {parseFloat(value).toFixed(2)}</span>;
  };

  const formatQuantita = (quantita) => {
    if (quantita == null) return <span className="text-muted">N/A</span>;
    const qty = parseInt(quantita);
    const isEsaurito = qty <= 0;
    const colorClass = isEsaurito ? 'text-danger fw-bold' : 'text-dark fw-bold';

    return (
      <span className={colorClass}>
        {isEsaurito && <FontAwesomeIcon icon={faExclamationTriangle} className="me-1 text-warning" size="xs" />}
        {qty}
      </span>
    );
  };

  const getGenereBadge = (genere) => {
    const generiConfig = {
      'Azione': { variant: 'danger', icon: faFilm },
      'Animazione': { variant: 'info', icon: faChild },
      'Commedia': { variant: 'warning', icon: faLaugh },
      'Documentario': { variant: 'secondary', icon: faBook },
      'Drammatico': { variant: 'primary', icon: faMasksTheater },
      'Fantasy': { variant: 'success', icon: faDragon },
      'Horror': { variant: 'dark', icon: faSkull },
      'Musical': { variant: 'info', icon: faMusic },
      'Romantico': { variant: 'danger', icon: faHeart },
      'Fantascienza': { variant: 'primary', icon: faRocket },
      'Thriller': { variant: 'dark', icon: faExclamationTriangle },
    };
    const config = generiConfig[genere] || { variant: 'secondary', icon: faTheaterMasks };
    return (
      <Badge bg={config.variant} className="d-inline-flex align-items-center gap-1" style={compactBadgeStyle} title={genere || ''}>
        {config.icon && <FontAwesomeIcon icon={config.icon} size="xs" />}
        {genere || 'N/A'}
      </Badge>
    );
  };

  const getSupportoBadge = (supporto) => {
    const supportiConfig = {
      'DVD': { variant: 'primary', icon: faCompactDisc },
      'Blu-ray': { variant: 'info', icon: faCompactDisc },
      '4K UHD': { variant: 'success', icon: faCompactDisc },
      'VHS': { variant: 'secondary', icon: faArchive },
      'CD': { variant: 'warning', icon: faCompactDisc },
      'Vinile': { variant: 'danger', icon: faMusic },
    };
    const config = supportiConfig[supporto] || { variant: 'secondary', icon: faArchive };
    return (
      <Badge bg={config.variant} className="d-inline-flex align-items-center" style={compactBadgeStyle}>
        {supporto || 'N/A'}
      </Badge>
    );
  };

  const getColumnValue = (film, columnName) => {
    if (columnName === 'immagine_url' && film.immagine_url) {
            return <img
              src={film.immagine_url && film.immagine_url.startsWith('http')
              ? film.immagine_url
              : `${process.env.REACT_APP_STATIC_FILES_BASE_URL}${film.immagine_url}`}
              alt={`Affiche del film ${film.titolo || ''} (${film.supporto || 'Supporto sconosciuto'})`} // Aggiunto alt prop
              onError={(e) => { e.target.style.display = 'none'; }}
            />;    }
    if (columnName === 'titolo') {
        const titleContent = film.titolo || 'N/A';
        if (film.immagine_url) {
            const popover = (
                <Popover id={`popover-film-${film.id}`}>
                    <Popover.Body>
                                                                                <img
                                                                                    src={film.immagine_url && film.immagine_url.startsWith('http') 
                                                                                        ? film.immagine_url 
                                                                                        : `${process.env.REACT_APP_STATIC_FILES_BASE_URL}${film.immagine_url}`}
                                                                                    alt={`Affiche del film ${film.titolo || ''} (${film.supporto || 'Supporto sconosciuto'})`}
                                                                                    style={{ maxWidth: '200px', height: 'auto' }}
                                                                                />                    </Popover.Body>
                </Popover>
            );

            return (
                <OverlayTrigger placement="right" overlay={popover} delay={{ show: 250, hide: 400 }}>
                    {/* Il click lo gestisce la riga (singolo → dettaglio, doppio → modifica): la navigazione immediata del Link impediva il doppio click */}
                    <Link to={`/film/${film.id}`} onClick={(e) => { if (!e.ctrlKey && !e.metaKey && !e.shiftKey) e.preventDefault(); }} style={{ textDecoration: 'none', color: 'inherit' }}>
                        <span>{titleContent}</span>
                    </Link>
                </OverlayTrigger>
            );
        }
        return titleContent;
    }
    if (columnName === 'fornitore' && film.fornitore) {
      return film.fornitore.denominazione;
    }
    if (columnName === 'condizione') {
      return getCondizioneBadge(film.condizione);
    }
    if (columnName === 'quantita') {
      return formatQuantita(film.quantita);
    }
    if (columnName === 'genere') {
      return getGenereBadge(film.genere);
    }
    if (columnName === 'supporto') {
      return getSupportoBadge(film.supporto);
    }
    if (['prezzo_negozio_base', 'prezzo_negozio', 'prezzo_ibs'].includes(columnName)) {
      return formatPrezzoVendita(film[columnName]);
    }
    if (columnName === 'costo_acquisto') {
      return formatCurrency(film[columnName]);
    }
    if (columnName === 'anno') {
      return film.anno || 'N/A';
    }
    if (columnName === 'da_pubblicare_su_ibs') {
        return (
            <Badge bg={film.da_pubblicare_su_ibs ? 'success' : 'danger'}>
                {film.da_pubblicare_su_ibs ? 'Sì' : 'No'}
            </Badge>
        );
    }
    if (columnName === 'in_vetrina') {
        return (
            <Badge bg={film.in_vetrina ? 'success' : 'danger'}>
                {film.in_vetrina ? 'Sì' : 'No'}
            </Badge>
        );
    }
    if (columnName === 'versione_noleggio') {
        return (
            <Badge bg={film.versione_noleggio ? 'info' : 'secondary'}>
                {film.versione_noleggio ? 'Noleggio' : 'Vendita'}
            </Badge>
        );
    }
    if (columnName === 'versione_editoriale') {
        return (
            <Badge bg={film.versione_editoriale ? 'warning' : 'secondary'}>
                {film.versione_editoriale ? 'Editoriale' : 'Standard'}
            </Badge>
        );
    }
    if (columnName === 'fuori_catalogo') {
        return (
            <Badge bg={film.fuori_catalogo ? 'warning' : 'success'}>
                {film.fuori_catalogo ? 'Fuori Catalogo' : 'In Catalogo'}
            </Badge>
        );
    }
    return film[columnName] !== undefined && film[columnName] !== null ? String(film[columnName]) : 'N/A';
  };

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: ({ table }) => (
          <Form.Check
            type="checkbox"
            onChange={(e) => {
                table.getToggleAllPageRowsSelectedHandler()(e);
                if (!e.target.checked) {
                    setIsSelectAllFiltered(false);
                }
            }}
            checked={table.getIsAllPageRowsSelected() || isSelectAllFiltered}
            disabled={films.length === 0}
          />
        ),
        cell: ({ row }) => (
          <Form.Check
            type="checkbox"
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            onClick={(e) => e.stopPropagation()}
          />
        ),
        minSize: 30,
        maxSize: 30,
        size: 30, // Larghezza fissa e minima per la colonna select
        enableResizing: false,
        enableSorting: false,
        enableColumnFilter: false,
      }),
      ...Object.entries(allColumnsMap).map(([key, value]) =>
        columnHelper.accessor(key, {
            header: () => value,
            cell: info => getColumnValue(info.row.original, key),
            enableColumnFilter: true,
            enableSorting: true, // Abilita esplicitamente il sorting
            meta: {
                filterVariant: ['genere', 'categoria', 'supporto', 'condizione', 'quantita', 'anno', 'durata', 'prezzo_negozio_base', 'prezzo_negozio', 'prezzo_ibs', 'casa_di_produzione', 'ubicazione', 'regista', 'attori'].includes(key) ? 'select' : (key === 'da_pubblicare_su_ibs' || key === 'in_vetrina' || key === 'versione_noleggio' || key === 'versione_editoriale' || key === 'fuori_catalogo') ? 'select' : 'text',
                filterOptions: (key === 'da_pubblicare_su_ibs' || key === 'in_vetrina' || key === 'versione_noleggio' || key === 'versione_editoriale' || key === 'fuori_catalogo') ? [{ value: true, label: 'Sì' }, { value: false, label: 'No' }] : distinctValues[key],
            },
        })
      ),
    ],
    [films, distinctValues]
  );

  const onColumnVisibilityChange = (updater) => {
    const newState = typeof updater === 'function' ? updater(columnVisibility) : updater;
    setColumnVisibility(newState);
    localStorage.setItem(visibilityKey, JSON.stringify(newState));
  };
  
  const table = useReactTable({
    data: films,
    columns,
    state: {
      columnVisibility,
      sorting,
      columnFilters,
      columnOrder,
      columnSizing,
    },
    onColumnVisibilityChange: onColumnVisibilityChange,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    enableColumnReordering: true,
    enableMultiSort: true,
    manualSorting: true, // Il sorting è gestito lato server, getSortedRowModel serve solo per l'UI
    manualFiltering: true,
  });

  const handleAutoResize = (column) => {
    autoResizeColumn(column, table);
  };

  const handleAutoFitAllColumns = () => {
    autoFitColumns(table);
  };

  const handleResetColumnWidths = () => {
    resetColumnWidths(table);
  };

  // Column presets
  const columnPresets = {
    essenziale: ['sku', 'titolo', 'anno', 'supporto', 'prezzo_negozio', 'prezzo_ibs', 'quantita', 'condizione', 'barcode', 'barcode_ibs', 'versione_noleggio', 'fuori_catalogo'],
    scheda_film: ['sku', 'titolo', 'genere', 'regista', 'attori', 'durata', 'casa_di_produzione', 'supporto', 'prezzo_negozio', 'quantita', 'condizione'],
    completa: Object.keys(allColumnsMap)
  };

  const applyColumnPreset = (presetName) => {
    const preset = columnPresets[presetName];
    if (preset) {
      const newVisibility = Object.keys(allColumnsMap).reduce((acc, key) => {
        acc[key] = preset.includes(key);
        return acc;
      }, {});
      setColumnVisibility(newVisibility);
      localStorage.setItem(visibilityKey, JSON.stringify(newVisibility));
    }
  };

  useEffect(() => {
    setSelectedFilmIds(table.getSelectedRowModel().rows.map(row => row.original.id));
    if (!table.getIsAllPageRowsSelected()) {
        setIsSelectAllFiltered(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table.getSelectedRowModel().rows]);

  const rowClickTimeoutRef = useRef(null);

  const handleRowClick = (row) => {
    if (rowClickTimeoutRef.current) {
      clearTimeout(rowClickTimeoutRef.current);
      rowClickTimeoutRef.current = null;
      return;
    }
    rowClickTimeoutRef.current = setTimeout(() => {
      navigate(`/film/${row.original.id}`);
      rowClickTimeoutRef.current = null;
    }, 250);
  };

  const sectionByColumn = {
    ubicazione: 'ubicazione',
    prezzo_negozio_base: 'prezzi',
    prezzo_negozio: 'prezzi',
    prezzo_ibs: 'prezzi',
  };

  const handleRowDoubleClick = (e, row) => {
    const columnId = e.target.closest('td')?.getAttribute('data-column-id');
    const section = sectionByColumn[columnId];
    navigate(`/film/edit/${row.original.id}${section ? `#${section}` : ''}`);
  };

  const handleExportSelected = async () => {
    setExporting(true);
    setError(null);
    try {
      const response = await exportFilmsCSV(selectedFilmIds);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'film_selezionati.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      table.toggleAllRowsSelected(false);
    } catch (err) {
      setError('Errore durante l\'esportazione dei film selezionati.');
    } finally {
      setExporting(false);
    }
  };

  const handleExportAllExcel = async () => {
    setExportingExcel(true);
    setError(null);
    try {
      const response = await exportFilmsExcel();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'films.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Errore durante l\'esportazione di tutti i film in Excel.');
    } finally {
      setExportingExcel(false);
    }
  };
  
    const handleExportIBS = async () => {
    setExportingIBS(true);
    setError(null);
    try {
      const response = await exportFilmIBS();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'film_ibs.csv'; // Default filename
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8'')?([^;]+)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Errore durante l\'esportazione per IBS.');
      console.error(err);
    } finally {
      setExportingIBS(false);
    }
  };

  const handleExportWebSelected = async () => {
    setExportingWeb(true);
    setError(null);
    try {
      const response = await exportFilmWeb(selectedFilmIds);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'magazzino_web_selezione.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Errore durante l\'esportazione per il sito web.');
      console.error(err);
    } finally {
      setExportingWeb(false);
    }
  };

  const handleExportAllWeb = async () => {
    setExportingAllWeb(true);
    setError(null);
    try {
        const filters = columnFilters.reduce((acc, filter) => {
                const { id, value } = filter;
                if (value && typeof value === 'object' && !Array.isArray(value) && value.__advanced) {
                    if (value.__advanced === 'not_empty') {
                        acc[`${id}__isnot`] = null;
                    } else if (value.__advanced === 'contains') {
                        acc[`${id}__ilike`] = value.text;
                    } else if (value.__advanced === 'not_contains') {
                        acc[`${id}__not_ilike`] = value.text;
                    }
                } else if (Array.isArray(value)) {
                    if (value.length > 0) {
                        acc[id] = { 'in': value };
                    }
                } else if (typeof value === 'string' && value.toUpperCase() === 'N/A') {
                    acc[id] = null;
                } else {
                    acc[id] = value;
                }
                return acc;
            }, {});

      const response = await exportFilmWebFiltered(JSON.stringify(filters));
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'magazzino_web_filtrato.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Errore durante l\'esportazione di tutti i film filtrati per il web.');
      console.error(err);
    } finally {
      setExportingAllWeb(false);
    }
  };

  const handlePrintWithLayout = async (layoutId) => {
    if (selectedFilmIds.length === 0) {
        alert("Seleziona almeno un film per la stampa.");
        return;
    }
    setLoading(true);
    try {
        const response = await generatePdf(layoutId, selectedFilmIds);
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `documento_layout_${layoutId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error("Errore durante la generazione del PDF", error);
        setError("Errore durante la generazione del PDF.");
    } finally {
        setLoading(false);
    }
  };

  const handleAssignImages = async () => {
    // Conferma elaborazione di TUTTI i film senza immagine
    const confirmMessage = 
      `🔍 ASSEGNAZIONE IMMAGINI MANCANTI\n\n` +
      `Questa funzione elaborerà TUTTI i film senza immagine nel database.\n\n` +
      `Per ogni film verrà cercata l'immagine:\n` +
      `1️⃣ Prima in LOCALE (cartella immagini_ean_locale) per barcode\n` +
      `2️⃣ Se non trovata, cerca su TMDB per titolo + anno\n\n` +
      `⏱️ Il processo potrebbe richiedere diversi minuti.\n` +
      `📊 Alla fine verrà mostrato un report completo con:\n` +
      `   • Immagini assegnate con successo\n` +
      `   • Film con problemi (con dettagli e report scaricabile)\n\n` +
      `Procedere con l'elaborazione di TUTTI i film?`;
    
    if (!window.confirm(confirmMessage)) return;
    
    // Usa un numero molto grande per elaborare tutti i film
    const numProducts = 999999;
    
    // Ottieni un token fresco prima di avviare lo streaming
    let token;
    try {
      const tokenResponse = await refreshToken();
      token = tokenResponse.data.access_token;
      localStorage.setItem('access_token', token);
    } catch (err) {
      alert('Impossibile aggiornare il token. Effettua nuovamente il login.');
      return;
    }
    
    const params = new URLSearchParams({
      max_products: numProducts,
      token: token
    });
    
    const url = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/film/assign-images-stream/?${params.toString()}`;
    
    setImageAssignProgress({ ...imageAssignProgress, active: true, status: 'running', processed: 0, total: 0 });
    
    const es = new EventSource(url);
    
    es.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === 'start') {
        setImageAssignProgress(prev => ({ ...prev, total: data.total }));
      } else if (data.type === 'progress') {
        setImageAssignProgress(prev => ({
          ...prev,
          processed: data.processed,
          current_product: data.product_title,
          status: 'running',
          success: data.status === 'success' ? prev.success + 1 : prev.success,
          failed: data.status === 'failure' ? prev.failed + 1 : prev.failed
        }));
      } else if (data.type === 'done') {
        setImageAssignProgress(prev => ({ 
          ...prev, 
          status: 'success', 
          current_product: 'Completato!', 
          success: data.success, 
          failed: data.failed,
          failed_report: data.failed_report || []
        }));
        refetchFilms();
        
        let message = `Completato!\n\nSuccessi: ${data.success}\nFalliti: ${data.failed}`;
        if (data.failed_report && data.failed_report.length > 0) {
          message += `\n\n⚠️ Ci sono ${data.failed_report.length} prodotti con errori.\nPuoi scaricare il report dettagliato cliccando sul bottone che apparirà sotto la progress bar.`;
        }
        alert(message);
        
        es.close();
      }
    };
    
    es.onerror = (err) => {
      console.error("Errore EventSource:", err);
      setImageAssignProgress(prev => ({ ...prev, status: 'error', current_product: 'Errore di connessione.' }));
      es.close();
      setTimeout(() => {
        setImageAssignProgress({ active: false, processed: 0, total: 0, current_product: '', status: 'idle', success: 0, failed: 0 });
      }, 5000);
    };
  };

  // Funzioni helper per download CSV reports
  const downloadFailedAssignReport = () => {
    if (!imageAssignProgress.failed_report || imageAssignProgress.failed_report.length === 0) {
      alert('Nessun report di errori disponibile.');
      return;
    }
    
    const csvContent = [
      ['ID', 'SKU', 'Titolo', 'Barcode', 'Cercato Locale', 'Cercato TMDB', 'Motivo errore'],
      ...imageAssignProgress.failed_report.map(item => [
        item.id,
        item.sku,
        item.titolo,
        item.barcode,
        item.searched_local || 'N/A',
        item.searched_tmdb || 'N/A',
        item.reason
      ])
    ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `immagini_assegnazione_fallite_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const downloadFailedRefreshReport = () => {
    if (!imageRefreshProgress.failed_report || imageRefreshProgress.failed_report.length === 0) {
      alert('Nessun report di errori disponibile.');
      return;
    }
    
    const csvContent = [
      ['ID', 'SKU', 'Titolo', 'Barcode', 'Motivo errore'],
      ...imageRefreshProgress.failed_report.map(item => [
        item.id,
        item.sku,
        item.titolo,
        item.barcode,
        item.reason
      ])
    ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `immagini_refresh_fallite_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const downloadLowQualityReport = () => {
    if (!imageRefreshProgress.low_quality_report || imageRefreshProgress.low_quality_report.length === 0) {
      alert('Nessun report di bassa qualità disponibile.');
      return;
    }
    
    const csvContent = [
      ['ID', 'SKU', 'Titolo', 'Barcode', 'Larghezza (px)', 'Altezza (px)', 'Peso (KB)', 'Problemi', 'File Sorgente'],
      ...imageRefreshProgress.low_quality_report.map(item => [
        item.id,
        item.sku,
        item.titolo,
        item.barcode,
        item.width,
        item.height,
        item.file_size_kb,
        item.issues,
        item.source_file
      ])
    ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `immagini_bassa_qualita_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const closeImageAssignProgress = () => {
    setImageAssignProgress({ 
      active: false, 
      processed: 0, 
      total: 0, 
      current_product: '', 
      status: 'idle', 
      success: 0, 
      failed: 0,
      failed_report: []
    });
  };

  const closeImageRefreshProgress = () => {
    setImageRefreshProgress({ 
      active: false, 
      processed: 0, 
      total: 0, 
      current_product: '', 
      status: 'idle', 
      success: 0, 
      failed: 0, 
      low_quality: 0, 
      improved_online: 0,
      failed_report: [],
      low_quality_report: []
    });
  };

  const handleRefreshImages = async () => {
    // Scelta strategia con spiegazione completa
    const strategiaScelta = prompt(
      `🔍 AGGIORNAMENTO QUALITÀ IMMAGINI\n\n` +
      `Questa funzione elaborerà TUTTE le immagini esistenti nel database.\n\n` +
      `Per ogni immagine verrà verificata la qualità (dimensioni, peso).\n` +
      `Se trovo immagini locali con qualità scarsa (< 400x400px), cosa devo fare?\n\n` +
      `Digita "ONLINE" = Cerca versione migliore su TMDB (ma potrebbe essere generica)\n` +
      `Digita "LOCALE" = Usa comunque locale e segnala nel report (CONSIGLIATO)\n\n` +
      `Annulla = Interrompi operazione`,
      'LOCALE'
    );
    
    if (!strategiaScelta) return; // Annullato
    
    const useOnline = strategiaScelta.toUpperCase().includes('ONLINE');
    
    // Conferma finale con riepilogo
    const confirmMessage = 
      `📊 CONFERMA ELABORAZIONE:\n\n` +
      `✅ Verranno elaborate TUTTE le immagini esistenti\n` +
      `✅ Strategia immagini scarse: ${useOnline ? 'Cerca ONLINE (TMDB)' : 'Usa LOCALE + report'}\n\n` +
      `⏱️ Il processo potrebbe richiedere diversi minuti.\n` +
      `📋 Alla fine riceverai un report completo con:\n` +
      `   • Immagini con qualità OK\n` +
      `   • Immagini di qualità BASSA (con report scaricabile)\n` +
      `   • Immagini migliorate da online\n\n` +
      `Procedere?`;
    
    if (!window.confirm(confirmMessage)) return;
    
    // Usa un numero molto grande per elaborare tutte le immagini
    const numProducts = 999999;
    
    // Streaming con progress bar
      
      // Streaming con progress bar
      if (!window.confirm(
        `CONFERMA:\n\n` +
        `- TUTTI i film con immagini esistenti verranno elaborati\n` +
        `- Strategia immagini scarse: ${useOnline ? 'Cerca ONLINE' : 'Usa LOCALE + report'}\n\n` +
        `Verrà mostrato il progresso in tempo reale.\n\n` +
        `Procedere?`
      )) return;

    
    // Ottieni un token fresco prima di avviare lo streaming
    let token;
    try {
      const tokenResponse = await refreshToken();
      token = tokenResponse.data.access_token;
      localStorage.setItem('access_token', token);
    } catch (err) {
      alert('Impossibile aggiornare il token. Effettua nuovamente il login.');
      return;
    }
    
    const params = new URLSearchParams({
      max_products: numProducts,
      use_online_fallback: useOnline,
      token: token
    });
    
    const url = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/film/refresh-images-stream/?${params.toString()}`;
    
    setImageRefreshProgress({ ...imageRefreshProgress, active: true, status: 'running', processed: 0, total: 0 });
    
    const es = new EventSource(url);
    
    es.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === 'start') {
        setImageRefreshProgress(prev => ({ ...prev, total: data.total }));
      } else if (data.type === 'progress') {
        setImageRefreshProgress(prev => ({
          ...prev,
          processed: data.processed,
          current_product: data.product_title,
          status: 'running',
          success: data.status === 'success' || data.status === 'warning' ? prev.success + 1 : prev.success,
          failed: data.status === 'failure' ? prev.failed + 1 : prev.failed,
          low_quality: data.status === 'warning' ? prev.low_quality + 1 : prev.low_quality
        }));
      } else if (data.type === 'done') {
        setImageRefreshProgress(prev => ({ 
          ...prev, 
          status: 'success', 
          current_product: 'Completato!', 
          success: data.success, 
          failed: data.failed,
          low_quality: data.low_quality || 0,
          improved_online: data.improved_online || 0,
          failed_report: data.failed_report || [],
          low_quality_report: data.low_quality_report || []
        }));
        refetchFilms();
        
        let message = `Completato!\n\n` +
          `Successi: ${data.success}\n` +
          `⚠️ Qualità bassa: ${data.low_quality || 0}\n` +
          `Migliorate da online: ${data.improved_online || 0}\n` +
          `Falliti: ${data.failed}`;
        
        if ((data.low_quality_report && data.low_quality_report.length > 0) || (data.failed_report && data.failed_report.length > 0)) {
          message += `\n\n⚠️ Ci sono problemi da risolvere.\nPuoi scaricare i report dettagliati cliccando sui bottoni che appariranno sotto la progress bar.`;
        }
        
        alert(message);
        es.close();
      }
    };
    
    es.onerror = (err) => {
      console.error("Errore EventSource:", err);
      setImageRefreshProgress(prev => ({ ...prev, status: 'error', current_product: 'Errore di connessione.' }));
      es.close();
      setTimeout(() => {
        setImageRefreshProgress({ active: false, processed: 0, total: 0, current_product: '', status: 'idle', success: 0, failed: 0, low_quality: 0, improved_online: 0 });
      }, 5000);
    };
  };



  const handlePopulateSelectedFilmsFromTmdb = () => {
    if (selectedFilmIds.length === 0) {
      alert('Seleziona almeno un film per popolare i dati da TMDB.');
      return;
    }
    if (window.confirm(`Sei sicuro di voler popolare i dati mancanti per i ${selectedFilmIds.length} film selezionati da TMDB? Questa operazione potrebbe richiedere del tempo.`)) {
      
      setLastReport({ updated_films: [], failed_films: [] }); // Pulisce il report precedente
      const params = new URLSearchParams();
      selectedFilmIds.forEach(id => params.append('film_ids', id));
      const accessToken = localStorage.getItem('access_token');
      if (accessToken) {
        params.append('token', accessToken);
      }
      const url = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/film/populate-stream/?${params.toString()}`;

      setPopulationProgress({ ...populationProgress, active: true, status: 'running', processed: 0, total: selectedFilmIds.length });

      const es = new EventSource(url);
      eventSourceRef.current = es;

      es.onopen = () => {
      };

      es.onmessage = (event) => {
        const data = JSON.parse(event.data);
        
        if (data.type === 'start') {
            setPopulationProgress(prev => ({ ...prev, total: data.total }));
        } else if (data.type === 'progress') {
            setPopulationProgress(prev => ({
                ...prev,
                processed: data.processed,
                current_film: data.film_title,
                status: 'running'
            }));
            // Aggrega i dati per il report finale
            if (data.status === 'success') {
                setLastReport(prev => ({
                    ...prev,
                    updated_films: [...prev.updated_films, { id: data.film_id, titolo: data.film_title }]
                }));
            } else if (data.status === 'failure') {
                setLastReport(prev => ({
                    ...prev,
                    failed_films: [...prev.failed_films, { id: data.film_id, titolo: data.film_title, reason: data.reason }]
                }));
            }
        } else if (data.type === 'done') {
            setPopulationProgress(prev => ({ ...prev, status: 'success', current_film: 'Completato!' }));
            refetchFilms(); // Refresh data
            setShowReportModal(true); // Mostra il report modale
            setTimeout(() => {
                setPopulationProgress({ active: false, processed: 0, total: 0, current_film: '', status: 'idle' });
            }, 3000);
            es.close();
            eventSourceRef.current = null;
        }
      };

      es.onerror = (err) => {
        console.error("Errore EventSource:", err);
        setPopulationProgress(prev => ({ ...prev, status: 'error', current_film: 'Errore di connessione.' }));
        setShowReportModal(true); // Mostra il report anche in caso di errore parziale
        es.close();
        eventSourceRef.current = null;
        setTimeout(() => {
            setPopulationProgress({ active: false, processed: 0, total: 0, current_film: '', status: 'idle' });
        }, 5000);
      };
    }
  };

  // Funzione per aggiornare i rating da TMDB
  const handleUpdateRatingsFromTmdb = () => {
    if (selectedFilmIds.length === 0) {
      alert('Seleziona almeno un film per aggiornare i rating da TMDB.');
      return;
    }
    
    if (window.confirm(`Vuoi aggiornare i rating di ${selectedFilmIds.length} film selezionati da TMDB? Questa operazione potrebbe richiedere alcuni minuti.`)) {
      const params = new URLSearchParams();
      selectedFilmIds.forEach(id => params.append('film_ids', id));
      const accessToken = localStorage.getItem('access_token');
      if (accessToken) {
        params.append('token', accessToken);
      }
      const url = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/film/update-ratings-stream/?${params.toString()}`;

      setRatingReport({ updated_films: [], skipped_films: [], failed_films: [] });
      setRatingUpdateProgress({
        active: true,
        status: 'running',
        processed: 0,
        total: selectedFilmIds.length,
        current_film: '',
        updated: 0,
        skipped: 0,
        errors: 0
      });

      const es = new EventSource(url);
      ratingEventSourceRef.current = es;

      es.onmessage = (event) => {
        const data = JSON.parse(event.data);
        
        if (data.type === 'start') {
          setRatingUpdateProgress(prev => ({ ...prev, total: data.total }));
        } else if (data.type === 'progress') {
          setRatingUpdateProgress(prev => ({
            ...prev,
            processed: data.processed,
            current_film: data.film_title,
            status: 'running',
            updated: data.status === 'success' ? prev.updated + 1 : prev.updated,
            skipped: data.status === 'skipped' ? prev.skipped + 1 : prev.skipped,
            errors: data.status === 'failure' ? prev.errors + 1 : prev.errors
          }));
          // Accumula dati per il report finale
          if (data.status === 'success') {
            setRatingReport(prev => ({ ...prev, updated_films: [...prev.updated_films, { id: data.film_id, titolo: data.film_title, reason: data.reason }] }));
          } else if (data.status === 'skipped') {
            setRatingReport(prev => ({ ...prev, skipped_films: [...prev.skipped_films, { id: data.film_id, titolo: data.film_title, reason: data.reason }] }));
          } else if (data.status === 'failure') {
            setRatingReport(prev => ({ ...prev, failed_films: [...prev.failed_films, { id: data.film_id, titolo: data.film_title, reason: data.reason }] }));
          }
        } else if (data.type === 'done') {
          setRatingUpdateProgress(prev => ({
            ...prev,
            status: 'success',
            current_film: `Completato! ✅ ${data.updated} aggiornati, ⚠️ ${data.skipped} saltati, ❌ ${data.errors} errori`,
            updated: data.updated,
            skipped: data.skipped,
            errors: data.errors
          }));
          refetchFilms();
          setShowRatingReportModal(true);
          setTimeout(() => {
            setRatingUpdateProgress({
              active: false,
              processed: 0,
              total: 0,
              current_film: '',
              status: 'idle',
              updated: 0,
              skipped: 0,
              errors: 0
            });
          }, 5000);
          es.close();
          ratingEventSourceRef.current = null;
        } else if (data.type === 'error') {
          setRatingUpdateProgress(prev => ({
            ...prev,
            status: 'error',
            current_film: `Errore: ${data.message}`
          }));
          es.close();
          ratingEventSourceRef.current = null;
        }
      };

      es.onerror = (err) => {
        console.error("Errore EventSource Rating:", err);
        setRatingUpdateProgress(prev => ({
          ...prev,
          status: 'error',
          current_film: 'Errore di connessione.'
        }));
        es.close();
        ratingEventSourceRef.current = null;
        setTimeout(() => {
          setRatingUpdateProgress({
            active: false,
            processed: 0,
            total: 0,
            current_film: '',
            status: 'idle',
            updated: 0,
            skipped: 0,
            errors: 0
          });
        }, 5000);
      };
    }
  };

  const handleUpdateAllRatingsFromTmdb = async () => {
    if (!window.confirm(
      `⭐ AGGIORNA RATING TMDB — TUTTI I FILM SENZA RATING\n\n` +
      `Questa operazione cercherà e aggiornerà il rating TMDB per TUTTI i film nel database che non hanno ancora un rating.\n\n` +
      `⏱️ Il processo potrebbe richiedere diversi minuti a seconda del numero di film.\n\n` +
      `Procedere?`
    )) return;

    let token;
    try {
      const tokenResponse = await refreshToken();
      token = tokenResponse.data.access_token;
      localStorage.setItem('access_token', token);
    } catch (err) {
      alert('Impossibile aggiornare il token. Effettua nuovamente il login.');
      return;
    }

    const params = new URLSearchParams({ token });
    const url = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/film/update-ratings-stream/?${params.toString()}`;

    setRatingReport({ updated_films: [], skipped_films: [], failed_films: [] });
    setRatingUpdateProgress({
      active: true,
      status: 'running',
      processed: 0,
      total: 0,
      current_film: '',
      updated: 0,
      skipped: 0,
      errors: 0
    });

    const es = new EventSource(url);
    ratingEventSourceRef.current = es;

    es.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.type === 'start') {
        setRatingUpdateProgress(prev => ({ ...prev, total: data.total }));
      } else if (data.type === 'progress') {
        setRatingUpdateProgress(prev => ({
          ...prev,
          processed: data.processed,
          current_film: data.film_title,
          status: 'running',
          updated: data.status === 'success' ? prev.updated + 1 : prev.updated,
          skipped: data.status === 'skipped' ? prev.skipped + 1 : prev.skipped,
          errors: data.status === 'failure' ? prev.errors + 1 : prev.errors
        }));
        if (data.status === 'success') {
          setRatingReport(prev => ({ ...prev, updated_films: [...prev.updated_films, { id: data.film_id, titolo: data.film_title, reason: data.reason }] }));
        } else if (data.status === 'skipped') {
          setRatingReport(prev => ({ ...prev, skipped_films: [...prev.skipped_films, { id: data.film_id, titolo: data.film_title, reason: data.reason }] }));
        } else if (data.status === 'failure') {
          setRatingReport(prev => ({ ...prev, failed_films: [...prev.failed_films, { id: data.film_id, titolo: data.film_title, reason: data.reason }] }));
        }
      } else if (data.type === 'done') {
        setRatingUpdateProgress(prev => ({
          ...prev,
          status: 'success',
          current_film: `Completato! ✅ ${data.updated} aggiornati, ⚠️ ${data.skipped} saltati, ❌ ${data.errors} errori`,
          updated: data.updated,
          skipped: data.skipped,
          errors: data.errors
        }));
        refetchFilms();
        setShowRatingReportModal(true);
        setTimeout(() => {
          setRatingUpdateProgress({ active: false, processed: 0, total: 0, current_film: '', status: 'idle', updated: 0, skipped: 0, errors: 0 });
        }, 5000);
        es.close();
        ratingEventSourceRef.current = null;
      } else if (data.type === 'error') {
        setRatingUpdateProgress(prev => ({ ...prev, status: 'error', current_film: `Errore: ${data.message}` }));
        es.close();
        ratingEventSourceRef.current = null;
      }
    };

    es.onerror = (err) => {
      console.error("Errore EventSource Rating (tutti):", err);
      setRatingUpdateProgress(prev => ({ ...prev, status: 'error', current_film: 'Errore di connessione.' }));
      es.close();
      ratingEventSourceRef.current = null;
      setTimeout(() => {
        setRatingUpdateProgress({ active: false, processed: 0, total: 0, current_film: '', status: 'idle', updated: 0, skipped: 0, errors: 0 });
      }, 5000);
    };
  };

  useEffect(() => {
    // Cleanup function to run when component unmounts
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (ratingEventSourceRef.current) {
        ratingEventSourceRef.current.close();
      }
    };
  }, []); // Empty dependency array means this runs once on mount and cleans up on unmount

  const deleteApiMap = { film: deleteFilm };
  const deleteMultipleApiMap = { film: deleteMultipleFilms };

  const renderPaginationItems = () => {
    const totalPages = Math.ceil(totalFilms / itemsPerPage);
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage, endPage;

    if (totalPages <= maxPagesToShow) {
        startPage = 1;
        endPage = totalPages;
    } else {
        if (currentPage <= Math.ceil(maxPagesToShow / 2)) {
            startPage = 1;
            endPage = maxPagesToShow;
        } else if (currentPage + Math.floor(maxPagesToShow / 2) >= totalPages) {
            startPage = totalPages - maxPagesToShow + 1;
            endPage = totalPages;
        } else {
            startPage = currentPage - Math.floor(maxPagesToShow / 2);
            endPage = currentPage + Math.floor(maxPagesToShow / 2);
        }
    }

    for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(
            <Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>
                {i}
            </Pagination.Item>
        );
    }
    return pageNumbers;
  };
  
  const handleResetFilters = () => {
    setReportType('all');
    setQuickSearch('');
    table.resetColumnFilters();
    setSorting([]);
  };

  const handleDownloadErrorReport = () => {
    if (!lastReport || !lastReport.failed_films || lastReport.failed_films.length === 0) {
      alert("Nessun dato di errore da esportare.");
      return;
    }

    const headers = ["ID Film", "Titolo", "Motivo del Fallimento"];
    const csvRows = [
      headers.join(';'),
      ...lastReport.failed_films.map(item => 
        [item.id, `"${item.titolo}"`, `"${item.reason}"`].join(';')
      )
    ];

    const csvString = csvRows.join('\n');
    const blob = new Blob(["\uFEFF" + csvString], { type: 'text/csv;charset=utf-8;' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'report_errori_tmdb.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrintReport = () => {
    const printContents = document.getElementById('report-printable-area').innerHTML;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Report Popolamento TMDB</title>
          <style>
            body { font-family: sans-serif; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>${printContents}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const hasManualFilters = table.getState().columnFilters.some(f => {
    if (reportType !== 'all' && f.id === 'quantita') {
        return false;
    }
    return true;
  });

  // Conta i filtri attivi per il badge
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    // Filtri colonna
    table.getState().columnFilters.forEach(f => {
      if (!(reportType !== 'all' && f.id === 'quantita')) {
        count++;
      }
    });
    return count;
  }, [table, reportType]);

  // Calcola statistiche per StatsCards
  const stats = useMemo(() => {
    return [
      { label: 'Totale Film', value: totalFilms, icon: faFilm, color: 'primary' },
      { label: 'Nuovi', value: serverStats.nuovi, icon: faStar, color: 'success' },
      { label: 'Usati', value: serverStats.usati, icon: faRecycle, color: 'info' },
      { label: 'Fuori Catalogo', value: serverStats.fuori_catalogo, icon: faArchive, color: 'warning' },
    ];
  }, [totalFilms, serverStats]);

  // Funzione per renderizzare il contenuto della card mobile
  const renderFilmCard = useCallback((film, isSelected, onToggleSelect) => {
    return (
      <Row>
        <Col xs={2} className="d-flex align-items-center justify-content-center">
          <Form.Check
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
            style={{ transform: 'scale(1.3)' }}
          />
        </Col>
        <Col xs={3}>
          {film.immagine_url ? (
            <img 
              src={film.immagine_url.startsWith('http') ? film.immagine_url : `${process.env.REACT_APP_STATIC_FILES_BASE_URL}${film.immagine_url}`} 
              alt={film.titolo}
              style={{ 
                width: '100%', 
                height: 'auto',
                maxHeight: '100px',
                objectFit: 'cover',
                borderRadius: '4px'
              }}
            />
          ) : (
            <div style={{ 
              width: '100%', 
              height: '80px',
              backgroundColor: '#e9ecef',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              fontSize: '0.7rem',
              color: '#6c757d'
            }}>
              No IMG
            </div>
          )}
        </Col>
        <Col xs={7}>
          <div>
            <strong style={{ fontSize: '0.9rem', display: 'block', marginBottom: '4px' }}>
              {film.titolo}
            </strong>
            <div style={{ fontSize: '0.75rem', color: '#6c757d', marginBottom: '2px' }}>
              ID: {film.id} {film.anno && `• ${film.anno}`}
            </div>
            {film.regista && (
              <div style={{ fontSize: '0.75rem', marginBottom: '2px' }}>
                <FontAwesomeIcon icon={faFilm} className="me-1" />
                {film.regista}
              </div>
            )}
            {(film.genere || film.supporto) && (
              <div style={{ fontSize: '0.7rem', marginBottom: '4px' }}>
                {film.genere && <Badge bg="secondary" className="me-1" style={{ fontSize: '0.65rem' }}>{film.genere}</Badge>}
                {film.supporto && <Badge bg="info" style={{ fontSize: '0.65rem' }}>{film.supporto}</Badge>}
              </div>
            )}
            <div className="d-flex gap-1 flex-wrap" style={{ fontSize: '0.7rem' }}>
              {film.quantita !== undefined && (
                <Badge bg={film.quantita > 0 ? 'success' : 'danger'} style={{ fontSize: '0.65rem' }}>
                  <FontAwesomeIcon icon={faBoxes} className="me-1" />
                  Qta: {film.quantita}
                </Badge>
              )}
              {film.prezzo_negozio && (
                <Badge bg="primary" style={{ fontSize: '0.65rem' }}>
                  <FontAwesomeIcon icon={faEuroSign} className="me-1" />
                  €{film.prezzo_negozio}
                </Badge>
              )}
              {film.condizione && (
                <Badge bg={film.condizione === 'Nuovo' ? 'success' : 'warning'} style={{ fontSize: '0.65rem' }}>
                  {film.condizione}
                </Badge>
              )}
            </div>
          </div>
        </Col>
      </Row>
    );
  }, []);

  // Configurazione filtri rapidi per mobile
  const quickFilters = useMemo(() => [
    {
      label: 'Tutti',
      icon: faFilm,
      isActive: reportType === 'all',
      onClick: () => setReportType('all'),
      variant: 'outline-primary',
      gradientActive: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
      color: '#007bff',
      borderColorActive: '#007bff'
    },
    {
      label: 'Magazzino',
      icon: faWarehouse,
      isActive: reportType === 'in_stock',
      onClick: () => setReportType('in_stock'),
      variant: 'outline-success',
      gradientActive: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
      color: '#28a745',
      borderColorActive: '#28a745'
    },
    {
      label: 'Esauriti',
      icon: faExclamationTriangle,
      isActive: reportType === 'out_of_stock',
      onClick: () => setReportType('out_of_stock'),
      variant: 'outline-danger',
      gradientActive: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
      color: '#dc3545',
      borderColorActive: '#dc3545'
    }
  ], [reportType]);

  // Contenuto dropdown azioni per mobile
  const actionsDropdownContent = useMemo(() => (
    <>
      <Dropdown.Header className="text-muted small">Importazioni</Dropdown.Header>
      <Dropdown.Item as={Link} to="/nuovo/film/import" className="py-2">
        <FontAwesomeIcon icon={faFileInvoice} className="me-2 text-primary" />
        Importa da CSV/Excel
      </Dropdown.Item>
      <Dropdown.Item as={Link} to="/nuovo/film-import-tmdb" className="py-2">
        <FontAwesomeIcon icon={faFilm} className="me-2 text-info" />
        Importa da TMDB
      </Dropdown.Item>
      <Dropdown.Item as={Link} to="/nuovo/film-import-cecchi-gori" className="py-2">
        <FontAwesomeIcon icon={faFileInvoice} className="me-2 text-warning" />
        Importa Cecchi Gori
      </Dropdown.Item>
      <Dropdown.Item as={Link} to="/nuovo/film-import-listino" className="py-2">
        <FontAwesomeIcon icon={faFileInvoice} className="me-2 text-success" />
        Importa Listino (dvd-store)
      </Dropdown.Item>
      <Dropdown.Divider />
      <Dropdown.Header className="text-muted small">Esportazioni</Dropdown.Header>
      <Dropdown.Item onClick={handleExportSelected} disabled={selectedFilmIds.length === 0 || exporting} className="py-2">
        <FontAwesomeIcon icon={faFileInvoice} className="me-2 text-success" />
        {exporting ? 'Esportazione...' : 'Esporta CSV'}
      </Dropdown.Item>
      <Dropdown.Item onClick={handleExportAllExcel} disabled={exportingExcel} className="py-2">
        <FontAwesomeIcon icon={faFileInvoice} className="me-2 text-success" />
        {exportingExcel ? 'Esportazione...' : 'Esporta Excel'}
      </Dropdown.Item>
      <Dropdown.Item onClick={handleExportIBS} disabled={exportingIBS} className="py-2">
        <FontAwesomeIcon icon={faFileInvoice} className="me-2 text-info" />
        {exportingIBS ? 'Esportazione...' : 'Esporta IBS'}
      </Dropdown.Item>
      <Dropdown.Item onClick={handleExportAllWeb} disabled={exportingAllWeb} className="py-2">
        <FontAwesomeIcon icon={faFileInvoice} className="me-2 text-primary" />
        {exportingAllWeb ? 'Esportazione...' : 'Esporta Web (Filtrati)'}
      </Dropdown.Item>
      <Dropdown.Item onClick={handleExportWebSelected} disabled={selectedFilmIds.length === 0 || exportingWeb} className="py-2">
        <FontAwesomeIcon icon={faFileInvoice} className="me-2 text-primary" />
        {exportingWeb ? 'Esportazione...' : 'Esporta Web (Selezione)'}
      </Dropdown.Item>
      <Dropdown.Divider />
      <Dropdown.Header className="text-muted small">Operazioni</Dropdown.Header>
      <Dropdown.Item onClick={handlePrintWithLayout} disabled={selectedFilmIds.length === 0} className="py-2">
        <FontAwesomeIcon icon={faFileInvoice} className="me-2 text-secondary" />
        Stampa con Layout
      </Dropdown.Item>
      <Dropdown.Item onClick={handleAssignImages} disabled={loading} className="py-2">
        <FontAwesomeIcon icon={faFilm} className="me-2 text-warning" />
        Assegna Immagini
      </Dropdown.Item>
      <Dropdown.Item onClick={handlePopulateSelectedFilmsFromTmdb} disabled={selectedFilmIds.length === 0 || loading || populationProgress.active} className="py-2">
        <FontAwesomeIcon icon={faFilm} className="me-2 text-info" />
        {populationProgress.active ? 'Popolazione...' : 'Popola Dati da TMDB'}
      </Dropdown.Item>
      <Dropdown.Item onClick={handleUpdateRatingsFromTmdb} disabled={selectedFilmIds.length === 0 || loading || ratingUpdateProgress.active} className="py-2">
        <FontAwesomeIcon icon={faStar} className="me-2 text-warning" />
        {ratingUpdateProgress.active ? 'Aggiornamento Rating...' : 'Aggiorna Rating TMDB ⭐'}
      </Dropdown.Item>
      <Dropdown.Item onClick={handleUpdateAllRatingsFromTmdb} disabled={loading || ratingUpdateProgress.active} className="py-2">
        <FontAwesomeIcon icon={faStar} className="me-2 text-warning" />
        {ratingUpdateProgress.active ? 'Aggiornamento Rating...' : 'Aggiorna Rating TMDB (Tutti senza rating) ⭐'}
      </Dropdown.Item>
      <Dropdown.Item onClick={() => setShowUpdateGenreModal(true)} className="py-2">
        <FontAwesomeIcon icon={faStar} className="me-2 text-warning" />
        Aggiorna Generi
      </Dropdown.Item>
      <Dropdown.Item onClick={handleDownloadErrorReport} disabled={!lastReport || !lastReport.failed_films || lastReport.failed_films.length === 0} className="py-2">
        <FontAwesomeIcon icon={faFileInvoice} className="me-2 text-danger" />
        Report Errori TMDB
      </Dropdown.Item>
    </>
  ), [exporting, exportingExcel, exportingIBS, exportingWeb, exportingAllWeb, populationProgress.active, ratingUpdateProgress.active, selectedFilmIds.length, loading, lastReport, handleExportSelected, handleExportAllExcel, handleExportIBS, handleExportAllWeb, handleExportWebSelected, handlePrintWithLayout, handleAssignImages, handlePopulateSelectedFilmsFromTmdb, handleUpdateRatingsFromTmdb, handleUpdateAllRatingsFromTmdb, handleDownloadErrorReport]);

  // Usiamo SortableHeader dal componente comune invece di definirlo localmente

  usePageTitle('Film');

  return (
    <>
      <StatsCards stats={stats} />

    <Card style={{ marginBottom: '80px' }}>
      <Card.Header className="sticky-card-header">
        {isMobile ? (
          // Mobile: componente riutilizzabile  
          <MobileListLayout
            entityName="film"
            entityNamePlural="film"
            createPath="/nuovo/film"
            createButtonText="Nuovo Film"  
            createButtonIcon={faPlus}
            actionsDropdownContent={actionsDropdownContent}
            quickFilters={quickFilters}
            searchPlaceholder="Cerca film..."
            searchValue={quickSearch}
            activeFiltersCount={activeFiltersCount}
            onSearchChange={(e) => setQuickSearch(e.target.value)}
            items={table.getRowModel().rows.map(row => row.original)}
            selectedIds={selectedFilmIds}
            renderCard={renderFilmCard}
            onCardClick={(film) => navigate(`/film/${film.id}`)}
            onToggleSelect={(id) => {
              const row = table.getRowModel().rows.find(r => r.original.id === id);
              if (row) row.toggleSelected();
            }}
            onDeleteMultiple={async (ids) => {
              await deleteMultipleApiMap['film'](ids);
              refetchFilms();
              setSelectedFilmIds([]);
              setIsSelectAllFiltered(false);
              if (table) table.toggleAllRowsSelected(false);
            }}
            deleteConfirmMessage={(count) => `Vuoi eliminare ${count} film selezionato/i?`}
            extraStickyActions={
              <Button 
                variant="primary" 
                size="sm"
                onClick={() => setShowBulkEditModal(true)}
                style={{ borderRadius: '8px', fontWeight: '600' }}
              >
                <FontAwesomeIcon icon={faCog} className="me-1" />
                Modifica
              </Button>
            }
          />
        ) : (
          // Desktop: Layout originale
          <Row className="align-items-center">
            <Col md={6} className="d-flex justify-content-start align-items-center">
              <Button as={Link} to="/nuovo/film" className="me-2" size="sm" variant="success">Nuovo Film</Button>
              <Dropdown className="me-2">
                <Dropdown.Toggle variant="primary" id="dropdown-import" size="sm">
                  Importa
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item as={Link} to="/nuovo/film/import">Importa da CSV/Excel</Dropdown.Item>
                  <Dropdown.Item as={Link} to="/nuovo/film-import-tmdb">Importa da TMDB</Dropdown.Item>
                  <Dropdown.Item as={Link} to="/nuovo/film-import-cecchi-gori">Importa Cecchi Gori</Dropdown.Item>
                  <Dropdown.Item as={Link} to="/nuovo/film-import-listino">Importa Listino (dvd-store)</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              
              <Dropdown className="me-2">
                <Dropdown.Toggle variant="info" id="dropdown-export" size="sm">
                  Esporta
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={handleExportSelected} disabled={selectedFilmIds.length === 0 || exporting}>
                    {exporting ? 'Esportazione...' : 'Esporta Selezionati (CSV)'}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={handleExportAllExcel} disabled={exportingExcel}>
                    {exportingExcel ? 'Esportazione...' : 'Esporta Tutto (Excel)'}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={handleExportIBS} disabled={exportingIBS}>
                    {exportingIBS ? 'Esportazione...' : 'Esporta per IBS'}
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={handleExportAllWeb} disabled={exportingAllWeb}>
                    {exportingAllWeb ? 'Esportazione...' : 'Esporta per Web (Tutti Filtrati)'}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={handleExportWebSelected} disabled={selectedFilmIds.length === 0 || exportingWeb}>
                    {exportingWeb ? 'Esportazione...' : 'Esporta per Web (Selezione)'}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>

              <Dropdown className="me-2">
                <Dropdown.Toggle variant="secondary" id="dropdown-basic" size="sm">
                  Azioni
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={handlePrintWithLayout} disabled={selectedFilmIds.length === 0}>
                      Stampa con Layout
                  </Dropdown.Item>

                  <Dropdown.Item onClick={handleAssignImages} disabled={loading}>
                    Assegna Immagini Mancanti
                  </Dropdown.Item>
                  <Dropdown.Item onClick={handleRefreshImages} disabled={loading}>
                    🔄 Aggiorna Qualità Immagini Esistenti
                  </Dropdown.Item>
                  <Dropdown.Item onClick={handlePopulateSelectedFilmsFromTmdb} disabled={selectedFilmIds.length === 0 || loading || populationProgress.active}>
                    {populationProgress.active ? 'Popolazione in corso...' : 'Popola Dati Selezionati da TMDB'}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={handleUpdateRatingsFromTmdb} disabled={selectedFilmIds.length === 0 || loading || ratingUpdateProgress.active}>
                    {ratingUpdateProgress.active ? 'Aggiornamento rating...' : '⭐ Aggiorna Rating TMDB Selezionati'}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={handleUpdateAllRatingsFromTmdb} disabled={loading || ratingUpdateProgress.active}>
                    {ratingUpdateProgress.active ? 'Aggiornamento rating...' : '⭐ Aggiorna Rating TMDB (Tutti senza rating)'}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => setShowUpdateGenreModal(true)}>
                    Aggiorna Generi da File
                  </Dropdown.Item>
                  <Dropdown.Item onClick={handleDownloadErrorReport} disabled={!lastReport || !lastReport.failed_films || lastReport.failed_films.length === 0}>
                    Report Errori TMDB
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>

            </Col>
            <Col md={6} className="d-flex justify-content-end align-items-center">
              <Form.Group className="me-2">
                <Form.Select onChange={(e) => setReportType(e.target.value)} value={reportType} size="sm">
                  <option value="all">Tutti i Film</option>
                  <option value="in_stock">In Magazzino</option>
                  <option value="out_of_stock">Esauriti</option>
                </Form.Select>
              </Form.Group>

              <Dropdown className="me-2" autoClose="outside">
                <Dropdown.Toggle variant="outline-secondary" id="dropdown-viste" size="sm">
                  Viste
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => applyColumnPreset('essenziale')}>
                    📋 Vista Essenziale
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => applyColumnPreset('scheda_film')}>
                    🎬 Vista Scheda Film
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => applyColumnPreset('completa')}>
                    📊 Vista Completa
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>

              <Dropdown className="me-2" autoClose="outside">
                <Dropdown.Toggle variant="outline-secondary" id="dropdown-columns" size="sm">
                  Colonne
                </Dropdown.Toggle>
                <Dropdown.Menu className="long-dropdown-menu p-0" style={{ minWidth: '500px' }}>
                  <div className="d-flex">
                    <div className="p-2" style={{ flex: 1, borderRight: '1px solid #dee2e6' }}>
                      <ColumnOrderController
                        table={table}
                        allColumnsMap={allColumnsMap}
                      />
                    </div>
                    <div className="p-2" style={{ flex: 1 }}>
                      <h6 className="fw-bold">Mostra Colonne</h6>
                      <div className="d-flex flex-column">
                        {table.getAllColumns().filter(column => column.id !== 'select').map(column => (
                          <Form.Check
                            key={column.id}
                            type="checkbox"
                            label={allColumnsMap[column.id]}
                            checked={column.getIsVisible()}
                            onChange={column.getToggleVisibilityHandler()}
                            className="dropdown-item"
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </Dropdown.Menu>
              </Dropdown>
              <Button
                variant="outline-secondary"
                size="sm"
                className="ms-2"
                onClick={handleAutoFitAllColumns}
                title="Adatta tutte le colonne al contenuto"
              >
                Auto-Fit
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                className="ms-2"
                onClick={handleResetColumnWidths}
                title="Ripristina larghezze predefinite"
              >
                Reset Colonne
              </Button>
              <Button
                  variant="outline-secondary"
                  size="sm"
                  className="ms-2"
                  onClick={handleResetFilters}
                  disabled={!hasManualFilters && reportType === 'all'}
              >
                  Azzera Filtri
              </Button>
            </Col>
          </Row>
        )}
      </Card.Header>
      <Card.Body>
        <InputGroup className="mb-2">
          <InputGroup.Text><FontAwesomeIcon icon={faSearch} /></InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Cerca per titolo, regista, attori, SKU, barcode..."
            value={quickSearch}
            onChange={e => setQuickSearch(e.target.value)}
          />
          {quickSearch && (
            <Button variant="outline-secondary" onClick={() => setQuickSearch('')}>
              <FontAwesomeIcon icon={faTimes} />
            </Button>
          )}
        </InputGroup>
        {populationProgress.active && (
          <ProgressBar 
            animated 
            now={populationProgress.total > 0 ? (populationProgress.processed / populationProgress.total) * 100 : 0} 
            label={`${populationProgress.processed}/${populationProgress.total} - ${populationProgress.current_film}`} 
            className="mb-3" 
            variant={populationProgress.status === 'error' ? 'danger' : 'info'}
          />
        )}
        {ratingUpdateProgress.active && (
          <div className="mb-3">
            <ProgressBar 
              animated 
              now={ratingUpdateProgress.total > 0 ? (ratingUpdateProgress.processed / ratingUpdateProgress.total) * 100 : 0} 
              className="mb-2" 
              variant={ratingUpdateProgress.status === 'error' ? 'danger' : ratingUpdateProgress.status === 'success' ? 'success' : 'warning'}
            />
            <div className="d-flex justify-content-between align-items-center">
              <small className="text-muted">
                <strong>Aggiornamento Rating:</strong> {ratingUpdateProgress.processed}/{ratingUpdateProgress.total} - {ratingUpdateProgress.current_film}
              </small>
              <div>
                <Badge bg="success" className="me-1">✅ {ratingUpdateProgress.updated}</Badge>
                <Badge bg="warning" className="me-1">⚠️ {ratingUpdateProgress.skipped}</Badge>
                <Badge bg="danger">❌ {ratingUpdateProgress.errors}</Badge>
              </div>
            </div>
          </div>
        )}
        {imageAssignProgress.active && (
          <div className="mb-3">
            <ProgressBar 
              animated={imageAssignProgress.status !== 'success'} 
              now={imageAssignProgress.total > 0 ? (imageAssignProgress.processed / imageAssignProgress.total) * 100 : 0} 
              label={`${imageAssignProgress.processed}/${imageAssignProgress.total} - ${imageAssignProgress.current_product}`} 
              className="mb-2" 
              variant={imageAssignProgress.status === 'error' ? 'danger' : imageAssignProgress.status === 'success' ? 'success' : 'info'}
            />
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="text-muted">
                <strong>Assegnazione Immagini:</strong> {imageAssignProgress.current_product}
              </small>
              <div>
                <Badge bg="success" className="me-1">✅ {imageAssignProgress.success}</Badge>
                <Badge bg="danger">❌ {imageAssignProgress.failed}</Badge>
              </div>
            </div>
            {imageAssignProgress.status === 'success' && (
              <div className="d-flex gap-2">
                {imageAssignProgress.failed_report && imageAssignProgress.failed_report.length > 0 && (
                  <Button size="sm" variant="warning" onClick={downloadFailedAssignReport}>
                    📥 Scarica Report Errori ({imageAssignProgress.failed_report.length})
                  </Button>
                )}
                <Button size="sm" variant="secondary" onClick={closeImageAssignProgress}>
                  Chiudi
                </Button>
              </div>
            )}
          </div>
        )}
        {imageRefreshProgress.active && (
          <div className="mb-3">
            <ProgressBar 
              animated={imageRefreshProgress.status !== 'success'} 
              now={imageRefreshProgress.total > 0 ? (imageRefreshProgress.processed / imageRefreshProgress.total) * 100 : 0} 
              label={`${imageRefreshProgress.processed}/${imageRefreshProgress.total} - ${imageRefreshProgress.current_product}`} 
              className="mb-2" 
              variant={imageRefreshProgress.status === 'error' ? 'danger' : imageRefreshProgress.status === 'success' ? 'success' : 'info'}
            />
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="text-muted">
                <strong>Aggiornamento Qualità:</strong> {imageRefreshProgress.current_product}
              </small>
              <div>
                <Badge bg="success" className="me-1">✅ {imageRefreshProgress.success}</Badge>
                <Badge bg="warning" className="me-1">⚠️ {imageRefreshProgress.low_quality}</Badge>
                <Badge bg="info" className="me-1">🌐 {imageRefreshProgress.improved_online}</Badge>
                <Badge bg="danger">❌ {imageRefreshProgress.failed}</Badge>
              </div>
            </div>
            {imageRefreshProgress.status === 'success' && (
              <div className="d-flex gap-2">
                {imageRefreshProgress.low_quality_report && imageRefreshProgress.low_quality_report.length > 0 && (
                  <Button size="sm" variant="warning" onClick={downloadLowQualityReport}>
                    📥 Scarica Report Bassa Qualità ({imageRefreshProgress.low_quality_report.length})
                  </Button>
                )}
                {imageRefreshProgress.failed_report && imageRefreshProgress.failed_report.length > 0 && (
                  <Button size="sm" variant="danger" onClick={downloadFailedRefreshReport}>
                    📥 Scarica Report Errori ({imageRefreshProgress.failed_report.length})
                  </Button>
                )}
                <Button size="sm" variant="secondary" onClick={closeImageRefreshProgress}>
                  Chiudi
                </Button>
              </div>
            )}
          </div>
        )}
        {loading && <div className="text-center"><Spinner animation="border" /></div>}
        {error && <Alert variant="danger">{error}</Alert>}
        {!error && !isMobile && (
            // Vista Desktop con Tabella (rimane montata durante i reload per evitare loop)
            <div style={{ maxHeight: 'calc(100vh - 350px)', overflow: 'auto', opacity: loading ? 0.4 : 1, pointerEvents: loading ? 'none' : 'auto' }}>
              <Table striped bordered hover style={{ width: table.getTotalSize() }} className="aligned-table">
                <thead>
                  {table.getHeaderGroups().map(headerGroup => (
                    <React.Fragment key={headerGroup.id}>
                      <tr>
                        {headerGroup.headers.map(header => (
                          <SortableHeader
                            key={header.id}
                            header={header}
                            table={table}
                            sorting={sorting}
                            setSorting={setSorting}
                            handleAutoResize={handleAutoResize}
                            stickyColumns={stickyColumns}
                          />
                        ))}
                      </tr>

                    </React.Fragment>
                  ))}
                </thead>
                <tbody>
                  {table.getIsAllPageRowsSelected() && !isSelectAllFiltered && (
                      <tr>
                          <td colSpan={table.getAllColumns().length} className="text-center bg-light">
                              Tutti i {table.getRowModel().rows.length} film in questa pagina sono selezionati.
                              <Button variant="link" onClick={() => {
                                  setIsSelectAllFiltered(true);
                                  setSelectedFilmIds([]); // We will use filters, not individual IDs
                              }}>
                                  Seleziona tutti i {totalFilms} film che corrispondono ai filtri
                              </Button>
                          </td>
                      </tr>
                  )}
                  {isSelectAllFiltered && (
                      <tr>
                          <td colSpan={table.getAllColumns().length} className="text-center bg-light">
                              Tutti i {totalFilms} film che corrispondono ai filtri sono selezionati.
                              <Button variant="link" onClick={() => {
                                  setIsSelectAllFiltered(false);
                                  table.toggleAllPageRowsSelected(false);
                              }}>
                                  Deseleziona tutto
                              </Button>
                          </td>
                      </tr>
                  )}
                  {table.getRowModel().rows.map(row => (
                    <tr key={row.id} onClick={() => handleRowClick(row)} onDoubleClick={(e) => handleRowDoubleClick(e, row)} className={row.getIsSelected() || isSelectAllFiltered ? 'table-primary' : ''} style={{ cursor: 'pointer', height: '40px' }}>
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} data-column-id={cell.column.id} style={{ verticalAlign: 'middle', padding: '0.4rem 0.4rem', width: `${cell.column.getSize()}px`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
        )}
        
        {/* Action Toolbar - Desktop version */}
        {!isMobile && (
          <ActionToolbar
            selectedItemIds={isSelectAllFiltered ? [-1] : selectedFilmIds} // Pass a dummy ID to enable buttons
            entityType="film"
            onDeleteSuccess={() => {
              refetchFilms();
              setIsSelectAllFiltered(false);
            }}
            deleteApiMap={deleteApiMap}
            deleteMultipleApiMap={deleteMultipleApiMap}
            onBulkEdit={() => setShowBulkEditModal(true)}
          />
        )}
      </Card.Body>
      {!isMobile && (
        <Card.Footer className="d-flex justify-content-between align-items-center">
        <Form.Group as={Row} className="align-items-center mb-0">
          <Col xs="auto">
            <Form.Label className="mb-0">Record per pagina</Form.Label>
          </Col>
          <Col xs="auto">
            <Form.Select
              size="sm"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Form.Select>
          </Col>
        </Form.Group>
        <Pagination size="sm" className="mb-0">
          <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
          <Pagination.Prev onClick={() => setCurrentPage(prev => prev - 1)} disabled={currentPage === 1} />
          {renderPaginationItems()}
          <Pagination.Next onClick={() => setCurrentPage(prev => prev + 1)} disabled={currentPage === Math.ceil(totalFilms / itemsPerPage)} />
          <Pagination.Last onClick={() => setCurrentPage(Math.ceil(totalFilms / itemsPerPage))} disabled={currentPage === Math.ceil(totalFilms / itemsPerPage)} />
        </Pagination>
      </Card.Footer>
      )}
    </Card>

    <Modal show={showReportModal} onHide={() => setShowReportModal(false)} size="lg">
        <Modal.Header closeButton>
            <Modal.Title>Report Popolamento TMDB</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <div id="report-printable-area">
                {lastReport && (
                    <>
                        <h5>Riepilogo</h5>
                        <p>{lastReport.updated_films.length} film aggiornati, {lastReport.failed_films.length} errori.</p>
                        
                        {lastReport.updated_films.length > 0 && (
                            <>
                                <h6 className="mt-4">Film Aggiornati Correttamente</h6>
                                <Table striped bordered size="sm">
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>Titolo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lastReport.updated_films.map(film => (
                                            <tr key={`s-${film.id}`}>
                                                <td>{film.id}</td>
                                                <td>{film.titolo}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </>
                        )}

                        {lastReport.failed_films.length > 0 && (
                            <>
                                <h6 className="mt-4">Film Non Aggiornati (Errori)</h6>
                                <Table striped bordered size="sm">
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>Titolo</th>
                                            <th>Motivo Errore</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lastReport.failed_films.map(film => (
                                            <tr key={`f-${film.id}`}>
                                                <td>{film.id}</td>
                                                <td>{film.titolo}</td>
                                                <td>{film.reason}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </>
                        )}
                    </>
                )}
            </div>
        </Modal.Body>
        <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowReportModal(false)}>
                Chiudi
            </Button>
            <Button variant="primary" onClick={handlePrintReport}>
                Stampa Report
            </Button>
        </Modal.Footer>
    </Modal>

    <Modal show={showRatingReportModal} onHide={() => setShowRatingReportModal(false)} size="lg">
        <Modal.Header closeButton>
            <Modal.Title>Report Aggiornamento Rating TMDB ⭐</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <h5>Riepilogo</h5>
            <p>
              <Badge bg="success" className="me-1">✅ {ratingReport.updated_films.length} aggiornati</Badge>
              <Badge bg="warning" className="me-1">⚠️ {ratingReport.skipped_films.length} saltati</Badge>
              <Badge bg="danger">❌ {ratingReport.failed_films.length} errori</Badge>
            </p>

            {ratingReport.updated_films.length > 0 && (
                <>
                    <h6 className="mt-3 text-success">Film Aggiornati Correttamente</h6>
                    <Table striped bordered size="sm">
                        <thead><tr><th style={{width: '60px'}}>ID</th><th>Titolo</th><th>Dettaglio</th></tr></thead>
                        <tbody>
                            {ratingReport.updated_films.map(film => (
                                <tr key={`ru-${film.id}`}>
                                    <td>{film.id}</td>
                                    <td>{film.titolo}</td>
                                    <td><small className="text-muted">{film.reason}</small></td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </>
            )}

            {ratingReport.skipped_films.length > 0 && (
                <>
                    <h6 className="mt-3 text-warning">Film Non Aggiornati (Saltati)</h6>
                    <Table striped bordered size="sm">
                        <thead><tr><th style={{width: '60px'}}>ID</th><th>Titolo</th><th>Motivo</th></tr></thead>
                        <tbody>
                            {ratingReport.skipped_films.map(film => (
                                <tr key={`rs-${film.id}`}>
                                    <td>{film.id}</td>
                                    <td>{film.titolo}</td>
                                    <td><small>{film.reason}</small></td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </>
            )}

            {ratingReport.failed_films.length > 0 && (
                <>
                    <h6 className="mt-3 text-danger">Film con Errori</h6>
                    <Table striped bordered size="sm">
                        <thead><tr><th style={{width: '60px'}}>ID</th><th>Titolo</th><th>Errore</th></tr></thead>
                        <tbody>
                            {ratingReport.failed_films.map(film => (
                                <tr key={`rf-${film.id}`}>
                                    <td>{film.id}</td>
                                    <td>{film.titolo}</td>
                                    <td><small className="text-danger">{film.reason}</small></td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </>
            )}
        </Modal.Body>
        <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowRatingReportModal(false)}>
                Chiudi
            </Button>
        </Modal.Footer>
    </Modal>

    <BulkEditFilmModal
      show={showBulkEditModal}
      onHide={() => setShowBulkEditModal(false)}
      selectedIds={!isSelectAllFiltered ? selectedFilmIds : []}
      filters={isSelectAllFiltered ? { columnFilters } : null}
      totalFiltered={isSelectAllFiltered ? totalFilms : 0}
      onSuccess={() => {
        refetchFilms();
        table.toggleAllPageRowsSelected(false);
        setIsSelectAllFiltered(false);
        setShowBulkEditModal(false);
      }}
    />
    <UpdateGenreModal
      show={showUpdateGenreModal}
      onHide={() => setShowUpdateGenreModal(false)}
      onUpdateSuccess={() => {
        refetchFilms();
        setShowUpdateGenreModal(false);
      }}
    />
    </>
  );
}

export default FilmList;